import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyLine = { item_id: '', counted_qty: '', remarks: '' };

function FoodPhysicalCount({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ store_id: '', count_type: 'CYCLE', blind_count: false, items: [{ ...emptyLine }] });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, ...extra });
  const parseResponse = async (res) => {
    const raw = await res.text();
    try { const json = raw ? JSON.parse(raw) : {}; return { data: json, message: json?.error || '' }; } catch { return { data: {}, message: raw || '' }; }
  };

  const load = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [sRes, iRes, cRes] = await Promise.all([
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/physical-count', { headers: headers() })
      ]);
      const s = await parseResponse(sRes); const i = await parseResponse(iRes); const c = await parseResponse(cRes);
      if (!sRes.ok) return setError(s.message || 'Failed to load stores');
      if (!iRes.ok) return setError(i.message || 'Failed to load items');
      if (!cRes.ok) return setError(c.message || 'Failed to load counts');
      setStores(Array.isArray(s.data) ? s.data : []);
      setItems(Array.isArray(i.data) ? i.data : []);
      setRows(Array.isArray(c.data) ? c.data : []);
    } catch { setError('Network error while loading physical count'); }
  };

  useEffect(() => {
    if (!chainId || !propertyId) return setError('Select a property first from Hotel Config');
    setError(''); setMessage(''); load();
  }, [token, chainId, propertyId]);

  const setLine = (idx, patch) => setForm((p) => ({ ...p, items: p.items.map((l, i) => i === idx ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((p) => ({ ...p, items: [...p.items, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const create = async () => {
    setError(''); setMessage('');
    if (!form.store_id) return setError('Select store');
    const lines = form.items.map((l) => ({ item_id: l.item_id, counted_qty: Number(l.counted_qty || 0), remarks: l.remarks || null })).filter((l) => l.item_id && l.counted_qty >= 0);
    if (!lines.length) return setError('Add at least one line');
    try {
      const res = await fetch('/api/v1/food/physical-count', {
        method: 'POST', headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ store_id: form.store_id, count_type: form.count_type, blind_count: form.blind_count, items: lines })
      });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Create physical count failed');
      setMessage('Physical count created');
      setForm({ store_id: '', count_type: 'CYCLE', blind_count: false, items: [{ ...emptyLine }] });
      load();
    } catch { setError('Network error while creating physical count'); }
  };

  const approve = async (id) => {
    setError(''); setMessage('');
    try {
      const res = await fetch(`/api/v1/food/physical-count/${id}/approve`, { method: 'POST', headers: headers() });
      const { message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Approve failed');
      setMessage('Physical count approved');
      load();
    } catch { setError('Network error while approving physical count'); }
  };

  const input = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btn = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Physical Stock Count</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}
      <div className="filter-bar">
        <h3>Create Count</h3>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr 1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} style={input}><option value="">Store</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}</select>
          <select value={form.count_type} onChange={(e) => setForm({ ...form, count_type: e.target.value })} style={input}><option value="CYCLE">Daily Cycle</option><option value="FULL">Full Count</option></select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><input type="checkbox" checked={form.blind_count} onChange={(e) => setForm({ ...form, blind_count: e.target.checked })} />Blind Count</label>
        </div>
        {form.items.map((l, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 2fr auto', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <select value={l.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value })} style={input}><option value="">Item</option>{items.map((i) => <option key={i.id} value={i.id}>{`${i.item_code} - ${i.item_name}`}</option>)}</select>
            <input type="number" min="0" step="0.001" value={l.counted_qty} onChange={(e) => setLine(idx, { counted_qty: e.target.value })} style={input} placeholder="Counted Qty" />
            <input value={l.remarks} onChange={(e) => setLine(idx, { remarks: e.target.value })} style={input} placeholder="Remarks" />
            <button onClick={() => removeLine(idx)} className="btn-danger" style={btn}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={addLine} className="btn-primary" style={btn}>Add Line</button>
          <button onClick={create} className="btn-success" style={btn}>Create Count</button>
        </div>
      </div>
      <div className="filter-bar">
        <h3>Count History</h3>
        {rows.length === 0 ? <p>No count records</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead><tr style={{ background: '#2f855a', color: '#fff' }}>
              <th style={{ padding: '0.5rem', textAlign: 'left' }}>Store</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Blind</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Variance</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th><th style={{ padding: '0.5rem', textAlign: 'left' }}>Action</th>
            </tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
              <td style={{ padding: '0.5rem' }}>{r.store_name}</td><td style={{ padding: '0.5rem' }}>{r.count_type}</td><td style={{ padding: '0.5rem' }}>{r.blind_count ? 'Yes' : 'No'}</td><td style={{ padding: '0.5rem' }}>{Number(r.total_variance || 0).toFixed(3)}</td><td style={{ padding: '0.5rem' }}>{r.status}</td><td style={{ padding: '0.5rem' }}>{r.status === 'PENDING_APPROVAL' && <button onClick={() => approve(r.id)} className="btn-success" style={btn}>Approve</button>}</td>
            </tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default FoodPhysicalCount;
